'use client'

import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ContactFormData, contactFormSchema } from "@/lib/schemas/contactFormSchema";
import { ACCEPTED_FILE_TYPES } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

export interface ContactFormProps {
      onFormSubmitted: (data: ContactFormData) => void;
}

const ContactForm = ({ onFormSubmitted }: ContactFormProps) => {
      const t = useTranslations('contact');
      const tCommon = useTranslations('_common');
      const recaptchaRef = useRef<ReCAPTCHA>(null);
      const [isSubmitting, setIsSubmitting] = useState(false);
      const router = useRouter();

      const form = useForm<ContactFormData>({
            resolver: zodResolver(contactFormSchema),
            defaultValues: {
                  name: "",
                  email: "",
                  message: "",
            },
      });

      const onSubmit = async (values: ContactFormData) => {
            try {
                  setIsSubmitting(true);
                  const captchaToken = await recaptchaRef.current?.executeAsync();

                  const formData = new FormData();
                  formData.append('name', values.name);
                  formData.append('email', values.email);
                  formData.append('message', values.message);
                  formData.append('captchaToken', captchaToken || '');

                  if (values.files) {
                        Array.from(values.files).forEach((file) => {
                              formData.append('files', file);
                        });
                  }

                  const response = await fetch('/api/contact', {
                        method: 'POST',
                        body: formData,
                  });

                  if (!response.ok) {
                        throw new Error('Failed to send message');
                  }

                  toast.success(t('success_message'));

                  form.reset();
                  recaptchaRef.current?.reset();

                  if (!!onFormSubmitted) {
                        onFormSubmitted(values);
                  }

            } catch (error) {
                  toast.error(t('error_message'));
            } finally {
                  setIsSubmitting(false);
            }
      };

      return (<Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                              <FormItem>
                                    <FormLabel>{t('name')}</FormLabel>
                                    <FormControl>
                                          <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                              </FormItem>
                        )}
                  />
                  <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                              <FormItem>
                                    <FormLabel>{t('email')}</FormLabel>
                                    <FormControl>
                                          <Input type="email" {...field} />
                                    </FormControl>
                                    <FormMessage />
                              </FormItem>
                        )}
                  />
                  <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                              <FormItem>
                                    <FormLabel>{t('message')}</FormLabel>
                                    <FormControl>
                                          <Textarea rows={5} {...field} />
                                    </FormControl>
                                    <FormMessage />
                              </FormItem>
                        )}
                  />
                  <FormField
                        control={form.control}
                        name="files"
                        render={({ field: { onChange, ...field } }) => (
                              <FormItem>
                                    <FormLabel>{t('attachments')}</FormLabel>
                                    <FormControl>
                                          <Input
                                                type="file"
                                                multiple
                                                accept={ACCEPTED_FILE_TYPES.join(',')}
                                                onChange={(e) => {
                                                      const files = e.target.files;
                                                      if (files) {
                                                            onChange(files);
                                                      }
                                                }}
                                                {...field}
                                                value={undefined}
                                          />
                                    </FormControl>
                                    <FormMessage />
                              </FormItem>
                        )}
                  />
                  <ReCAPTCHA
                        ref={recaptchaRef}
                        size="invisible"
                        sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
                  />
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                        {isSubmitting ? t('sending') : t('send')}
                  </Button>
            </form>
      </Form>);
}


export default ContactForm;